import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, catchError, finalize, of, take } from 'rxjs';

import { AlertPriority, AlertRule, AlertType } from 'src/app/models/alert';
import { AlertRuleService } from 'src/app/services/alert-rule.service';
import { SettingsService } from 'src/app/services/settings.service';
import { UserService } from 'src/app/services/user.service';
import { SettingsNotificationService } from 'src/app/settings/shared/services/settings-notifications.service';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import {
  FormAction,
  FormState,
  IntegrationsFormService,
} from '../user-settings-integrations/services/integrations-form.service';
import { PanelData } from '../../settings.constants';

/** Static config: which alert types the panel shows, grouped and labelled. */
interface AlertTypeGroupConfig {
  heading: string;
  types: { type: AlertType; label: string; disabledTooltip?: string }[];
}

interface AlertRuleRow {
  alertType: AlertType;
  label: string;
  isDisabled: boolean;
  disabledTooltip: string;
}

interface AlertRuleSectionView {
  heading: string;
  rows: AlertRuleRow[];
}

/** Shown on a disabled row whose alert type has no copy of its own. */
const DEFAULT_DISABLED_TOOLTIP = _('This alert is unavailable because a required setting is off.');

const isInstitutionSettingOff = (
  settingKey: string | undefined,
  institutionSettings: Record<string, unknown> | undefined,
): boolean => !!settingKey && institutionSettings?.[settingKey] !== true;

/** Marked for extraction; translated in the template. */
const ALERT_TYPE_GROUPS: AlertTypeGroupConfig[] = [
  {
    heading: _('Events'),
    types: [
      { type: AlertType.EVENT_NO_SHOW, label: _('Missing organizer') },
      { type: AlertType.TEACHER_DECLINED_EVENT, label: _('Decline event') },
    ],
  },
  {
    heading: _('Safeguarding'),
    types: [
      {
        type: AlertType.SESSION_NUDITY,
        label: _('Nudity'),
        disabledTooltip: _(
          'Turn on nudity detection in Settings → AI Assist to receive these alerts.',
        ),
      },
      {
        type: AlertType.POST_SESSION_SAFEGUARDING,
        label: _('Post-session safeguarding issues'),
        disabledTooltip: _(
          'Turn on AI safeguarding in Settings → AI Assist to receive these alerts.',
        ),
      },
    ],
  },
];

@UntilDestroy()
@Component({
  selector: 'app-user-settings-alerts',
  templateUrl: './user-settings-alerts.component.html',
  styleUrls: ['./user-settings-alerts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsAlertsComponent implements OnInit, OnDestroy {
  @Input() isMobile = false;
  @Input() panelData?: PanelData;
  @Output() goBack = new EventEmitter<void>();
  @Output() formChangedEvent = new EventEmitter<boolean>();

  readonly IconTypes = IconTypes;
  readonly AlertType = AlertType;
  readonly FormState = FormState;

  /** One group per alert type, keyed by it so the template can bind by name. */
  readonly form = this.formBuilder.group({});

  /** The rules exactly as the API returned them. */
  private alertRules: AlertRule[] = [];

  readonly isLoading$ = new BehaviorSubject<boolean>(true);
  readonly sections$ = new BehaviorSubject<AlertRuleSectionView[]>([]);
  readonly formState$ = this.formsService.getFormState$();

  constructor(
    private readonly alertRuleService: AlertRuleService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly formsService: IntegrationsFormService,
    private readonly settingsService: SettingsService,
    private readonly settingsNotificationService: SettingsNotificationService,
    private readonly userService: UserService,
  ) {}

  ngOnInit(): void {
    this.alertRuleService
      .getAlertRules()
      .pipe(
        catchError(() => {
          this.settingsService.showToast({ title: _('Failed to load alert settings') }, true);
          return of([] as AlertRule[]);
        }),
        finalize(() => this.isLoading$.next(false)),
        untilDestroyed(this),
      )
      .subscribe((alertRules) => this.buildForm(alertRules));

    this.formsService
      .getFormState$()
      .pipe(untilDestroyed(this))
      .subscribe((formState) => this.formChangedEvent.emit(formState !== FormState.Initial));

    this.formsService
      .getFormAction$()
      .pipe(untilDestroyed(this))
      .subscribe((action) => {
        if (action === FormAction.SUBMIT) {
          this.saveAlertRules();
        }
      });
  }

  ngOnDestroy(): void {
    this.formsService.cleanup();
  }

  onCancel(): void {
    this.formsService.dispatchAction(FormAction.CANCEL);
  }

  onSubmit(): void {
    this.formsService.dispatchAction(FormAction.SUBMIT);
  }

  onPriorityChange(alertType: AlertType, priority: AlertPriority): void {
    this.form.get(alertType)?.patchValue({ defaultPriority: priority });
  }

  /**
   * Sends every editable rule back whole, with the form's values merged in.
   * PUT /alerts/rules writes what it receives, so a partial payload would drop
   * channels and visibleTo to their schema defaults and silence the alert.
   */
  private saveAlertRules(): void {
    const formValue = this.form.getRawValue();
    const alertRules = this.alertRules
      .filter((alertRule) => !!formValue[alertRule.alertType])
      .map((alertRule) => ({ ...alertRule, ...formValue[alertRule.alertType] }));

    this.alertRuleService
      .updateAlertRules(alertRules)
      .pipe(
        take(1),
        finalize(() => this.formsService.setLoading(false)),
        untilDestroyed(this),
      )
      .subscribe({
        next: (savedAlertRules) => {
          this.alertRules = savedAlertRules;
          this.formsService.updateInitialValue(this.form.getRawValue());
          this.settingsNotificationService.showSuccessSettingsUpdateNotification();
        },
        error: () => {
          this.settingsNotificationService.showFailureSettingsUpdateNotification();
        },
      });
  }

  /** Controls are one group per alert type, so the set depends on what the API returned. */
  private buildForm(alertRules: AlertRule[]): void {
    const renderedTypes = new Set(
      ALERT_TYPE_GROUPS.flatMap((group) => group.types.map(({ type }) => type)),
    );

    for (const alertRule of alertRules) {
      if (!renderedTypes.has(alertRule.alertType)) {
        continue;
      }

      this.form.addControl(
        alertRule.alertType,
        this.formBuilder.group({
          enabled: [alertRule.enabled],
          defaultPriority: [alertRule.defaultPriority],
        }),
        { emitEvent: false },
      );
    }

    this.alertRules = alertRules;
    this.sections$.next(this.buildSections(alertRules));
    this.formsService.registerForm(this.form, this.form.getRawValue());
  }

  private buildSections(alertRules: AlertRule[]): AlertRuleSectionView[] {
    const ruleByType = new Map(alertRules.map((alertRule) => [alertRule.alertType, alertRule]));
    // Read once: the settings live on another panel, so they can't change while this one is open.
    const institutionSettings = this.userService.user.value?.user?.institution?.settings as
      | Record<string, unknown>
      | undefined;

    return ALERT_TYPE_GROUPS.map((group) => ({
      heading: group.heading,
      // Filtering first states the contract: the API omits rules whose capability
      // is off, and those rows are simply not rendered.
      rows: group.types
        .filter(({ type }) => ruleByType.has(type))
        .map(({ type, label, disabledTooltip }) => ({
          alertType: type,
          label,
          isDisabled: isInstitutionSettingOff(
            ruleByType.get(type)?.institutionSettingKey,
            institutionSettings,
          ),
          disabledTooltip: disabledTooltip ?? DEFAULT_DISABLED_TOOLTIP,
        })),
    })).filter((section) => section.rows.length > 0);
  }
}
